import { Link } from 'react-router-dom'
import { LEGAL, TERMS_PATH } from '../lib/legal'
import { LegalPage, Fact, ContactEmail } from '../components/legal/legal-page'

/**
 * Privacy Policy (SOL-197). DRAFT for legal review.
 *
 * Written from an inventory of the code on 2026-10-21, not from a template:
 * the processors are the services the Worker and the SPA actually call, the
 * retention periods are the TTLs actually set, and what is NOT collected (no
 * analytics, no tracking cookies, no email addresses in logs) was checked.
 * A new processor, log field or stored value must land here in the same change.
 */
export default function Privacy(): JSX.Element {
  return (
    <LegalPage title="Privacy Policy">
      <section>
        <h2>1. Who is responsible</h2>
        <p>
          {LEGAL.operator}, {LEGAL.operatorDescription}, at{' '}
          <Fact value={LEGAL.address} label="registered address" />, is the controller of the
          personal data described here. Contact us about anything in this policy at{' '}
          <ContactEmail />.
        </p>
        <p>
          Our representative in the European Union is{' '}
          <Fact value={LEGAL.euRepresentative} label="EU representative (GDPR Art. 27)" />.
        </p>
        <p>
          <strong>Photographs of other people.</strong> When a photographer uploads photographs of
          their clients, it is the photographer who decides what is uploaded and with whom it is
          shared. For that content the photographer is the controller and we are their processor,
          under the data processing terms in section 11 of our{' '}
          <Link to={TERMS_PATH}>Terms of Service</Link>. If you appear in a photographer's
          pictures, please contact the photographer first; we will help them answer you. When a
          photographer hands a Memoir over to their client, the client becomes its owner and,
          from then on, the one who decides about the data in it.
        </p>
      </section>

      <section>
        <h2>2. What we collect</h2>
        <ul>
          <li>
            <strong>Your account:</strong> your email address. Our sign-in provider also records
            when and from where you sign in (IP address and browser), to keep your account secure.
            If you had no account when a photographer shared a Memoir with you, it was created
            then, with the email address they entered.
          </li>
          <li>
            <strong>Your purchases:</strong> your name, billing address, VAT number if you give
            one, and your payment details. These are entered at, and held by, Stripe. We receive a
            customer reference, your email address and whether a payment succeeded, was refunded or
            was disputed. We never see your full card number. For each paid year of a Memoir we
            record which account paid for it.
          </li>
          <li>
            <strong>What you upload:</strong> your photographs and other files, their file names,
            sizes and upload dates, the names you give your Memoirs and albums, and the smaller
            copies we make of images for display.
          </li>
          <li>
            <strong>Access log:</strong> for each Memoir, a record of who opened the Memoir or an
            album, downloaded photographs or a ZIP, or prepared a download, and when: the account
            email of the person (or, for a view link, "someone with a view link"), what they did, and how many
            photographs, with the album name, the account's identifier, and, for a view link, a
            non-reversible identifier for the link and when it was created. It holds no IP addresses, no browser or device details and no view-link
            tokens.
          </li>
          <li>
            <strong>Sharing:</strong> the share codes and view links you create, and which account
            redeemed a code. When a whole Memoir is shared with a client, we record on that Memoir
            the client's email address and account, when they were invited and whether they
            accepted, and, once it has moved to them, that they are its owner and when it was
            handed over.
          </li>
          <li>
            <strong>Technical data:</strong> to limit abuse, we count requests per IP address and
            per email address, storing each only as a one-way hash that is deleted automatically
            within 24 hours. Our servers keep operational logs for a few days; we do not write
            email addresses into them.
          </li>
          <li>
            <strong>On your device:</strong> your browser's local storage holds what keeps you
            signed in and a copy of your album list, so the app loads quickly. We use no
            advertising or analytics, and no tracking cookies.
          </li>
        </ul>
      </section>

      <section>
        <h2>3. Why, and on what basis</h2>
        <ul>
          <li>
            To provide what you bought: your account, storing and showing your photographs,
            sharing, downloads, and sending your sign-in codes. Basis: our contract with you.
          </li>
          <li>
            To meet tax and accounting obligations, including keeping the evidence of your
            location that EU VAT rules require. Basis: legal obligation.
          </li>
          <li>
            To keep Memoar secure and prevent abuse, such as rate limiting and protecting against
            runaway uploads. Basis: our legitimate interest in a safe, working service.
          </li>
          <li>
            To tell people who joined our waitlist about Memoar. Basis: your consent, which you
            can withdraw at any time by unsubscribing.
          </li>
        </ul>
        <p>We do not sell personal data, and we do not use your photographs to train AI models.</p>
      </section>

      <section>
        <h2>4. Who processes it for us</h2>
        <ul>
          <li>
            <strong>Cloudflare</strong> runs our servers and stores your files and data. Your
            photographs are stored in Cloudflare's Eastern Europe region; other data, such as album
            details, is replicated across Cloudflare's global network.
          </li>
          <li><strong>WorkOS</strong> (USA) manages accounts and sign-in.</li>
          <li>
            <strong>Stripe</strong> processes payments, calculates VAT and issues invoices. For
            payment data Stripe also acts as an independent controller under its own privacy
            policy.
          </li>
          <li>
            <strong>Postmark</strong> (USA) delivers our emails to you: your sign-in codes,
            invitations to a shared Memoir and notices about your Memoirs.
          </li>
          <li>
            <strong>Mailchimp</strong> (USA) holds the email addresses of people who join our
            waitlist from memoar.me, and sends them news about Memoar.
          </li>
        </ul>
        <p>
          Some of these providers process data in the United States. Cloudflare and Stripe are
          certified under the EU-US Data Privacy Framework, which the European Commission
          recognises as adequate protection. For WorkOS, Postmark and Mailchimp, and for the others should
          that certification lapse, the transfer is covered by the European Commission's Standard
          Contractual Clauses in each provider's data processing agreement. Each provider is
          bound by such an agreement with us.
        </p>
        <p>
          Our website memoar.me loads nothing from third parties: its typefaces are served from
          our own site.
        </p>
      </section>

      <section>
        <h2>5. How long we keep it</h2>
        <ul>
          <li>
            <strong>Your photographs</strong> stay until you delete them, their Memoir is deleted
            because a year was not paid for or its purchase was refunded (see section 7 of our{' '}
            <Link to={TERMS_PATH}>Terms of Service</Link>), or you close your account. Deleting a
            photograph removes it from storage at once; a copy already loaded in someone's browser can remain
            in that browser's cache for up to about a day.
          </li>
          <li>
            <strong>The access log</strong> is kept for the life of the Memoir and deleted with
            it, completely by the following day. When a Memoir is handed over to a client, its
            whole log, including the entries from before, passes to the new owner, and the previous
            owner no longer sees it.
          </li>
          <li>
            <strong>Encryption keys:</strong> when a Memoir is deleted, its key is destroyed after
            the deletion is complete. Backups of the keys are kept for 7 days, so a destroyed key
            normally disappears from every copy within a week.
          </li>
          <li><strong>ZIP downloads</strong> we prepare for you are deleted after 7 days.</li>
          <li><strong>Rate-limiting records</strong> are deleted within 24 hours.</li>
          <li>
            <strong>Waitlist addresses</strong> are kept until you unsubscribe, which every email
            lets you do in one click.
          </li>
          <li>
            <strong>Your account</strong> is kept while it exists. If you close it with Delete my
            account on the Account page, we delete it, your Memoirs and their photographs within 30
            days of your request, and tell you the exact date. Until then you can cancel, and your
            Memoirs are read-only: you can view and download everything, but not upload. On the
            last day you can only cancel. A payment that reaches us for an account being closed is
            refunded. If you instead write to <ContactEmail /> from the email address you sign in
            with, we delete them within 30 days of your request and tell you the date.
          </li>
          <li>
            <strong>An account created by an invitation</strong> to a shared Memoir holds only its
            email address. It is kept, even if it is never used, until you delete it from the
            Account page or ask us to delete it.
          </li>
          <li>
            <strong>When your account is deleted</strong>, so is your account at WorkOS, and the
            access logs of your Memoirs go with them. A Memoir you shared with a client who had
            accepted is not deleted: it passes to them, with its access log, unless its purchase
            had been refunded or disputed, or the client was closing their account too. Albums other people shared with you stay
            with them. If you deleted it with Delete my account on the Account page, your email
            address and account identifier are also removed from those albums' access logs (the
            entry stays, without saying who it was), and we keep a record that the account was
            closed, with its internal identifier and the dates of the request and the deletion but
            not your email address, so that a payment started before the deletion and reaching us
            after it is refunded. The record that each deleted Memoir existed, which holds no
            name and no photographs, keeps the internal identifier of the account that bought it
            and, if it had been handed over, of its owner.
            Stripe keeps your customer record and invoices (section 4).
          </li>
          <li>
            <strong>Purchase and tax records</strong> are kept for as long as tax law requires,
            which for EU VAT evidence is up to 10 years.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. Security</h2>
        <p>
          Data travels to and from Memoar encrypted. Every photograph in storage, including the
          smaller display copies and the ZIP downloads we prepare, is encrypted with a key
          derived from a key that belongs to its Memoir alone. Those Memoir keys are held by
          Memoar, wrapped by a master key that is kept as a protected secret of our servers,
          outside storage. A copy of the storage, or of the access credentials to it, therefore
          yields no readable photographs.
        </p>
        <p>
          Apart from the staff and legal cases described below, we decrypt a photograph only to serve someone entitled to it: the owner of the Memoir,
          a client who claimed the album or accepted the Memoir's invitation, or a person with a
          view link. After a Memoir is handed over, its previous owner's account no longer has
          access to it; view links they created keep working, for whoever has them, until the new
          owner revokes them. Memoar has no admin
          screen for browsing customers' photographs. Display copies such as thumbnails and
          previews may be kept by Cloudflare's network, our network provider, for up to about 25
          hours, and browsers may keep display copies for up to about a day and originals for up to an hour.
        </p>
        <p>
          Memoar is not end-to-end encrypted: our systems can decrypt your photographs, as they
          must to make display copies and ZIP downloads.
        </p>
        <p>
          Access is logged. In the app, the owner of a Memoir can see who opened the Memoir or
          one of its albums, downloaded photographs or a ZIP, or prepared a download, and when.
          A client who claimed albums sees the entries for those albums only, where an opening
          through the owner's view link reads "someone with a view link". Repeated opens within
          30 minutes appear as one entry. After a handover the new owner sees the whole log,
          including the entries from before it, and the previous owner no longer sees any of it.
          The log records no IP addresses. If we cannot write an entry, the person is still given
          access.
        </p>
        <p>
          Our staff open your content only at the written request of the photographer or of the
          client the album was shared with, and that access appears in the log as support
          access, saying whose request it was. If the law compels us to give access, we do it with a deliberate procedure that is
          logged too, unless the law forbids us to tell you, in which case it may not appear in
          your log.
        </p>
      </section>

      <section>
        <h2>7. Your rights</h2>
        <p>
          You can ask us for a copy of your personal data, to correct it, to delete it, to restrict
          or object to how we use it, and to receive it in a portable form. Your photographs can be
          downloaded at any time as ZIP files from the app. You can delete your
          account yourself from the Account page (section 5). For anything else, write to{' '}
          <ContactEmail />. We answer within one month.
        </p>
        <p>
          You also have the right to complain to the data protection authority in the EU country
          where you live or work.
        </p>
      </section>

      <section>
        <h2>8. Children</h2>
        <p>
          Memoar accounts are not intended for anyone under 16. Photographs of children may of
          course be uploaded by the adults responsible for them, as in any family or event
          photography.
        </p>
      </section>

      <section>
        <h2>9. Changes</h2>
        <p>
          If we change this policy we will update the date above and, for a change that affects
          you materially, tell you by email first. Buying a Memoir is also governed by our{' '}
          <Link to={TERMS_PATH}>Terms of Service</Link>.
        </p>
      </section>
    </LegalPage>
  )
}
